import "./preloadTripperWidgetLocales";
import ReactDOM from "react-dom/client";
import { i18nLanguage } from "i18n";
import { TripperWidgetProviders } from "./TripperWidgetProviders";
import { TripperWidgetRouter } from "./TripperWidgetRouter";
import { TripperDisplayMode } from "./types";
import { fetchWidgetConfig } from "./fetchWidgetConfig";
import { isBusinessTypeFiltersEnabled } from "./utils/businessTypeFilters";

function isDebugEnabled(element: HTMLElement): boolean {
  if (element.getAttribute("debug") === "true") return true;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("wa-debug") === "1";
  } catch {
    return false;
  }
}

/**
 * Global guard: prevent duplicate bootstrapping if the embed script is
 * included more than once on the same page. Module scripts with identical
 * URLs are deduplicated by the browser, but this covers edge cases
 * (cache-busted URLs, non-module inclusions, bundler quirks).
 */
declare global {
  interface Window {
    __WA_TRIPPER_WIDGET__?: true;
  }
}

if (!window.__WA_TRIPPER_WIDGET__) {
  window.__WA_TRIPPER_WIDGET__ = true;

  const mountApp = (
    root: ReactDOM.Root,
    widgetId: string,
    accessId: string,
    itineraryId: string,
    language: i18nLanguage,
    displayMode: TripperDisplayMode,
    label?: string,
    fontFamilyOverride?: string,
    primaryColorOverride?: string,
    preview?: boolean,
    atlasMode?: boolean,
    forceDesktop?: boolean,
    verticalStack?: boolean,
    businessTypeFiltersEnabled?: boolean,
    onAtlasModeControllerReady?: (
      setAtlasMode: (value: boolean) => void,
    ) => void,
    onForceDesktopControllerReady?: (
      setForceDesktop: (value: boolean) => void,
    ) => void,
    onVerticalStackControllerReady?: (
      setVerticalStack: (value: boolean) => void,
    ) => void,
    onBusinessTypeFiltersEnabledReady?: (
      setEnabled: (value: boolean) => void,
    ) => void,
    debug?: boolean,
  ) => {
    root.render(
      <TripperWidgetProviders
        widgetId={widgetId}
        accessId={accessId}
        itineraryId={itineraryId}
        language={language}
        fontFamilyOverride={fontFamilyOverride}
        primaryColorOverride={primaryColorOverride}
        preview={preview}
        atlasMode={atlasMode}
        forceDesktop={forceDesktop}
        verticalStack={verticalStack}
        businessTypeFiltersEnabled={businessTypeFiltersEnabled}
        onAtlasModeControllerReady={onAtlasModeControllerReady}
        onForceDesktopControllerReady={onForceDesktopControllerReady}
        onVerticalStackControllerReady={onVerticalStackControllerReady}
        onBusinessTypeFiltersEnabledReady={onBusinessTypeFiltersEnabledReady}
        disableShadowDOM={displayMode === "print"}
        debug={debug}
      >
        <TripperWidgetRouter
          itineraryId={itineraryId}
          language={language}
          displayMode={displayMode}
          label={label}
        />
      </TripperWidgetProviders>,
    );
  };

  class WhereaboutsTripperWidget extends HTMLElement {
    /** React root — also serves as a per-element mount guard. */
    private _root: ReactDOM.Root | null = null;
    private _setAtlasMode: ((value: boolean) => void) | null = null;
    private _setForceDesktop: ((value: boolean) => void) | null = null;
    private _setVerticalStack: ((value: boolean) => void) | null = null;
    private _setBusinessTypeFiltersEnabled: ((value: boolean) => void) | null =
      null;

    static get observedAttributes() {
      return ["atlas-mode", "force-desktop", "vertical-stack", "type-filters"];
    }

    attributeChangedCallback(name: string) {
      if (name === "atlas-mode") {
        this._setAtlasMode?.(this.attr("atlas-mode") === "true");
        return;
      }
      if (name === "force-desktop") {
        this._setForceDesktop?.(this.attr("force-desktop") === "true");
        return;
      }
      if (name === "vertical-stack") {
        this._setVerticalStack?.(this.attr("vertical-stack") === "true");
        return;
      }
      if (name === "type-filters") {
        this._setBusinessTypeFiltersEnabled?.(
          isBusinessTypeFiltersEnabled(this.attr("type-filters")),
        );
      }
    }

    private attr(name: string): string | undefined {
      return this.getAttribute(name) || undefined;
    }

    connectedCallback() {
      // Per-element guard: skip if already mounted (e.g. element moved in the DOM).
      if (this._root) return;

      const widgetId = this.attr("widget-id");
      const accessId = this.attr("access-id");
      const debug = isDebugEnabled(this);

      if (!widgetId) {
        throw new Error(
          `${this.tagName}: missing required attribute widget-id`,
        );
      }
      if (!accessId) {
        throw new Error(
          `${this.tagName}: missing required attribute access-id`,
        );
      }

      const itineraryId = this.attr("itinerary-id");

      // Fast path: all required data is in the HTML — mount immediately.
      // Used by tripperPage.ts SSR and existing embeds with full attributes.
      if (itineraryId) {
        this.mount(widgetId, accessId, itineraryId, undefined, debug);
        return;
      }

      // Config path: fetch widget config from server, then mount.
      this.init(widgetId, accessId, debug).catch(err => {
        console.error("WhereaboutsTripperWidget: initialization failed", err);
        if (debug) {
          // Mount with a placeholder itinerary so the debug panel still renders
          this.mount(widgetId, accessId, "__debug_only__", undefined, true);
        } else {
          this.innerHTML =
            '<div style="display:grid;place-items:center;height:100%;min-height:200px;font-family:system-ui;color:#6F6E69;font-size:14px">' +
            (err.message === "widget_paused"
              ? "This widget is currently unavailable."
              : "Unable to load widget.") +
            "</div>";
        }
      });
    }

    disconnectedCallback() {
      if (this._root) {
        this._root.unmount();
        this._root = null;
      }
    }

    private async init(widgetId: string, accessId: string, debug: boolean) {
      const config = await fetchWidgetConfig(widgetId);
      // Element may have been removed while config was loading.
      if (!this.isConnected) return;
      this.mount(widgetId, accessId, config.itineraryId, config, debug);
    }

    private mount(
      widgetId: string,
      accessId: string,
      itineraryId: string,
      config?: {
        displayMode?: string;
        label?: string | null;
        accentColor?: string | null;
      },
      debug?: boolean,
    ) {
      const displayMode = (this.attr("display-mode") ??
        config?.displayMode ??
        "inline") as TripperDisplayMode;
      const label = this.attr("label") ?? config?.label ?? undefined;
      const primaryColor =
        this.attr("primary-color") ?? config?.accentColor ?? undefined;
      const lang = (this.attr("lang") ?? "en") as i18nLanguage;
      const fontFamilyOverride = this.attr("font-family-override");
      const preview = this.attr("preview") === "true";
      const atlasMode = this.attr("atlas-mode") === "true";
      const forceDesktop = this.attr("force-desktop") === "true";
      const verticalStack = this.attr("vertical-stack") === "true";
      const businessTypeFiltersEnabled = isBusinessTypeFiltersEnabled(
        this.attr("type-filters"),
      );

      // Default host-element layout. Custom elements are display:inline by
      // default, which collapses width:100% children to zero in shrinkwrap
      // parents. text-button is the only mode that should flow inline with
      // surrounding text.
      if (displayMode === "text-button") {
        this.style.display = "inline-block";
      } else {
        this.style.display = "block";
        this.style.width = "100%";
      }

      // Apply height attribute for inline display modes
      const isInline =
        displayMode === "inline" ||
        displayMode === "stacked" ||
        displayMode === "map-only";
      const height = this.attr("height");
      if (isInline) {
        const defaultHeight = verticalStack ? "100%" : "100svh";
        this.style.height = height
          ? /^\d+$/.test(height)
            ? `${height}px`
            : height
          : defaultHeight;
        if (verticalStack) {
          this.style.maxHeight = "100%";
          this.style.minHeight = "0";
        }
      }

      this._root = ReactDOM.createRoot(this);

      mountApp(
        this._root,
        widgetId,
        accessId,
        itineraryId,
        lang,
        displayMode,
        label || undefined,
        fontFamilyOverride,
        primaryColor || undefined,
        preview,
        atlasMode,
        forceDesktop,
        verticalStack,
        businessTypeFiltersEnabled,
        setAtlasMode => {
          this._setAtlasMode = setAtlasMode;
        },
        setForceDesktop => {
          this._setForceDesktop = setForceDesktop;
        },
        setVerticalStack => {
          this._setVerticalStack = setVerticalStack;
        },
        setBusinessTypeFiltersEnabled => {
          this._setBusinessTypeFiltersEnabled = setBusinessTypeFiltersEnabled;
        },
        debug,
      );
    }
  }

  customElements.define("whereabouts-tripper-widget", WhereaboutsTripperWidget);
}
